import React, { useState, useRef, useEffect } from 'react';
import { destinations, categories } from '@/data/destinations';
import { useAuth } from '@/contexts/AuthContext';
import { MapPin, ArrowRight, Heart } from 'lucide-react';


const categoryLabels: Record<string, string> = {
  all: 'All Destinations',
  beach: 'Beaches & Coast',
  heritage: 'Heritage Cities',
  mountain: 'Mountains & Alps',
  nature: 'Nature & Lakes',
};

interface DestinationModalProps {
  destination: typeof destinations[0] | null;
  onClose: () => void;
}

const DestinationModal: React.FC<DestinationModalProps> = ({ destination, onClose }) => {
  const { isDestinationSaved, saveDestination, unsaveDestination } = useAuth();

  if (!destination) return null;

  const isSaved = isDestinationSaved(destination.id);

  const handleToggleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isSaved) {
      await unsaveDestination(destination.id);
    } else {
      await saveDestination({
        id: destination.id,
        name: destination.name,
        location: destination.location,
        image: destination.image,
        price: destination.price,
      });
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          <img
            src={destination.image}
            alt={destination.alt || `${destination.name} — ${destination.location}, Albania luxury travel destination`}
            className="w-full h-64 md:h-80 object-cover"
          />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
            aria-label="Close"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L13 13M13 1L1 13" stroke="#2C2C2C" strokeWidth="1.5" />
            </svg>
          </button>
          <button
            onClick={handleToggleSave}
            className="absolute top-4 left-4 w-10 h-10 bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
            aria-label={isSaved ? 'Remove from saved' : 'Save destination'}
          >
            <Heart
              className={`w-5 h-5 transition-colors ${
                isSaved ? 'text-red-500 fill-red-500' : 'text-[#999]'
              }`}
            />
          </button>
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
            <span className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961]">
              {destination.region}
            </span>
          </div>
        </div>
        <div className="p-8 md:p-10">
          <h3 className="font-serif text-3xl md:text-4xl text-[#1A3B5C] mb-2">
            {destination.name}
          </h3>
          <div className="flex items-center gap-2 mb-6">
            <MapPin className="w-3.5 h-3.5 text-[#C9A961]" />
            <span className="font-sans text-xs tracking-wider text-[#666]">
              {destination.location}
            </span>
          </div>
          <p className="font-sans text-sm leading-relaxed text-[#555] mb-8">
            {destination.description}
          </p>
          <div className="flex flex-wrap gap-3 mb-8">
            {destination.features.map((feature) => (
              <span
                key={feature}
                className="font-sans text-[10px] tracking-[0.15em] uppercase px-4 py-2 bg-[#F5F1E8] text-[#1A3B5C]"
              >
                {feature}
              </span>
            ))}
          </div>
          <div className="flex items-center justify-end border-t border-[#E8DFD0] pt-6">
            <button
              onClick={() => {
                onClose();
                const el = document.getElementById('plan-journey');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="font-sans text-[10px] tracking-[0.2em] uppercase px-8 py-3.5 bg-[#1A3B5C]/10 text-[#1A3B5C] hover:bg-[#1A3B5C]/20 transition-colors"
            >
              Book Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DestinationsGrid: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [selectedDestination, setSelectedDestination] = useState<typeof destinations[0] | null>(null);
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());
  const gridRef = useRef<HTMLDivElement>(null);
  const { isDestinationSaved, saveDestination, unsaveDestination } = useAuth();

  const filteredDestinations = activeCategory === 'all'
    ? destinations
    : destinations.filter((d) => d.category === activeCategory);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = Number(entry.target.getAttribute('data-id'));
            setVisibleItems((prev) => new Set([...prev, id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const cards = gridRef.current?.querySelectorAll('[data-id]');
    cards?.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, [filteredDestinations]);

  const handleToggleSave = async (e: React.MouseEvent, dest: typeof destinations[0]) => {
    e.stopPropagation();
    if (isDestinationSaved(dest.id)) {
      await unsaveDestination(dest.id);
    } else {
      await saveDestination({
        id: dest.id,
        name: dest.name,
        location: dest.location,
        image: dest.image,
        price: dest.price,
      });
    }
  };

  return (
    <section id="destinations" className="py-24 lg:py-32 bg-[#FAF8F3]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961] block mb-4">
            Our Collection
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1A3B5C] font-light mb-6 gold-underline">
            Albanian Destinations
          </h2>
          <p className="font-sans text-sm text-[#777] max-w-lg mx-auto leading-relaxed mt-10">
            From the turquoise Riviera to the soaring Alps, each destination reveals a different facet of Albania's extraordinary beauty.
          </p>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-14">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 transition-all duration-300 ${
                activeCategory === cat
                  ? 'bg-[#1A3B5C] text-white'
                  : 'bg-transparent text-[#777] hover:text-[#1A3B5C] border border-[#ddd] hover:border-[#1A3B5C]'
              }`}
            >
              {categoryLabels[cat]}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {filteredDestinations.map((dest, index) => {
            const saved = isDestinationSaved(dest.id);
            return (
              <div
                key={dest.id}
                data-id={dest.id}
                className={`group cursor-pointer transition-all duration-700 ${
                  visibleItems.has(dest.id)
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
                onClick={() => setSelectedDestination(dest)}
              >
                <div className="img-zoom relative aspect-[4/3] mb-5">
                  <img
                    src={dest.image}
                    alt={dest.alt || `${dest.name} — ${dest.location}, Albania travel destination`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-[#1A3B5C]/0 group-hover:bg-[#1A3B5C]/40 transition-all duration-500 flex items-center justify-center">
                    <span className="font-sans text-[10px] tracking-[0.3em] uppercase text-white opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-center gap-2">
                      Explore <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                  {/* Save/Heart Button */}
                  <button
                    onClick={(e) => handleToggleSave(e, dest)}
                    className="absolute top-4 left-4 w-9 h-9 bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-all opacity-0 group-hover:opacity-100"
                    aria-label={saved ? 'Remove from saved' : 'Save destination'}
                  >
                    <Heart
                      className={`w-4 h-4 transition-colors ${
                        saved ? 'text-red-500 fill-red-500' : 'text-[#999] hover:text-red-400'
                      }`}
                    />
                  </button>
                  {/* Always show heart if saved */}
                  {saved && (
                    <button
                      onClick={(e) => handleToggleSave(e, dest)}
                      className="absolute top-4 left-4 w-9 h-9 bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-all group-hover:hidden"
                      aria-label="Remove from saved"
                    >
                      <Heart className="w-4 h-4 text-red-500 fill-red-500" />
                    </button>
                  )}
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-3 h-3 text-[#C9A961]" />
                    <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999]">
                      {dest.region}
                    </span>
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl text-[#1A3B5C] group-hover:text-[#C9A961] transition-colors mb-2">
                    {dest.name}
                  </h3>
                  <p className="font-sans text-xs text-[#888] leading-relaxed mb-3 line-clamp-2">
                    {dest.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const el = document.getElementById('plan-journey');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 bg-[#1A3B5C]/10 text-[#1A3B5C] hover:bg-[#1A3B5C]/20 transition-colors"
                    >
                      Book Now
                    </button>
                    <span className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#C9A961] flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      View Details <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal */}
      {selectedDestination && (
        <DestinationModal
          destination={selectedDestination}
          onClose={() => setSelectedDestination(null)}
        />
      )}
    </section>
  );
};

export default DestinationsGrid;
